import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, LineChart, Line, ComposedChart, Area,
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Cell
} from "recharts";
import SectionHeader from "../components/ui/SectionHeader";
import ChartCard from "../components/ui/ChartCard";
import MetricCard from "../components/ui/MetricCard";
import GaugeCard from "../components/ui/GaugeCard";
import MapPlaceholder from "../components/ui/MapPlaceholder";
import ChartTooltip from "../components/ui/ChartTooltip";
import { useTranslate } from "../i18n/useTranslate";
import { simulationScenarios } from "../data/mockData";
import { googleColor } from "../data/googleColors";
import { chartColor, gridProps, axisTick, legendStyle } from "../data/chartTheme";
import { statusHex, statusClasses, thresholdStatus } from "../data/statusColors";
import { useIntelligence } from "../intelligence/useIntelligence";
import { LiveTwinSection } from "../components/live/LiveOpsSections";
import { StaticContentDivider } from "../components/live/primitives";
import {
  Play, Pause, RefreshCw, Clock, Users, AlertTriangle, CheckCircle,
  ChevronDown, ChevronUp, CloudRain, Thermometer, Wind, Droplets,
  Cpu, Database, Wifi, Activity, Navigation, Shield, Zap,
  GitBranch, Timer, MapPin, TrendingUp, BarChart2, Eye, Radio
} from "lucide-react";

// ── static data ───────────────────────────────────────────────────
const EVACUATION_ROUTES = [
  { route:"North Exit — Mumbai-Nashik NH3",  time:18, capacity:80000,  status:"Clear",      load:42, lanes:4, vehicles:"Buses + Personal" },
  { route:"East Exit — Nashik Road Station", time:24, capacity:60000,  status:"Moderate",   load:68, lanes:2, vehicles:"Train + Walk"    },
  { route:"South Exit — Gangapur Road",      time:32, capacity:120000, status:"Clear",       load:38, lanes:6, vehicles:"All modes"       },
  { route:"Godavari River Route",            time:45, capacity:20000,  status:"Restricted",  load:91, lanes:1, vehicles:"Foot only"       },
  { route:"West Exit — Panchvati Road",      time:28, capacity:50000,  status:"Moderate",    load:74, lanes:3, vehicles:"Buses + Walk"    },
  { route:"Emergency Trimbak Corridor",      time:38, capacity:35000,  status:"Clear",       load:28, lanes:2, vehicles:"Emergency only"  },
];

const SCENARIO_PROJECTIONS = [
  { hour:"T+0",  density:92, crowdFlow:68000, risk:8.4, evacuated:0     },
  { hour:"T+30", density:88, crowdFlow:71000, risk:8.1, evacuated:28000 },
  { hour:"T+60", density:84, crowdFlow:74000, risk:7.8, evacuated:58000 },
  { hour:"T+90", density:79, crowdFlow:78000, risk:7.4, evacuated:91000 },
  { hour:"T+120",density:74, crowdFlow:82000, risk:7.0, evacuated:128000},
  { hour:"T+150",density:68, crowdFlow:76000, risk:6.5, evacuated:162000},
  { hour:"T+180",density:62, crowdFlow:68000, risk:5.8, evacuated:194000},
];

const SCENARIO_DETAILS = {
  1: {
    trigger:"Shahi Snan — Simhastha Peak Crowd Day",
    inputs:["Weather: Clear, 38°C","River: Normal","All gates open","Extra 800 police deployed"],
    outcomes:["Peak 9,00,000 pilgrims at 15:00","Ramkund reaches 9.8/m² density","5 critical zones simultaneously"],
    preposition:["Deploy SRP Company 3 at Ramkund Entry 2 by 12:00","Pre-position 3 ambulances at Camp Beta","Open Camp Delta shelter overflow at 13:30"],
    riskPeak:"9.2/10 at T+120min",
  },
  2: {
    trigger:"Godavari Bridge sudden closure — structural alert",
    inputs:["Bridge sensors: Vibration alert","1,80,000 pedestrians diverted","Alternate: NH3 + Gangapur Road"],
    outcomes:["6,20,000 pilgrims rerouted in 45 min","Panchvati bottleneck hits 8.4/m²","Transit time +22 min for all zones"],
    preposition:["Immediate police cordon at bridge both ends","Deploy 40 buses to Gangapur alternate route","Broadcast advisory within 5 min of closure"],
    riskPeak:"7.4/10 at T+45min",
  },
  3: {
    trigger:"Emergency mass evacuation — threat level RED",
    inputs:["Security alert — RED level","All zones: Evacuate","All exits open simultaneously"],
    outcomes:["6,84,000 pilgrims to be evacuated","Minimum clearance time: 21 min","6 exits active concurrently"],
    preposition:["All police cordon gates immediately","NDRF teams at all 6 exits","Medical teams at exit points for vulnerable pilgrims"],
    riskPeak:"Critical — protocol override active",
  },
  4: {
    trigger:"Godavari flood watch — monsoon river surge",
    inputs:["River level: 8.2m (flood watch at 9m)","Rain: 42mm forecast","Riverside ghats at risk"],
    outcomes:["4,20,000 pilgrims in riverside zones alerted","Ramkund Ghat evacuated by T+30","Gangapur Camp remains operational"],
    preposition:["Evacuate all riverside ghats by T+30","Deploy NDRF water rescue teams","Close Godavari bridge to pedestrians pre-emptively"],
    riskPeak:"6.8/10 at T+30min",
  },
};

const SENSOR_FEEDS = [
  { id:"CAM-001", name:"Ramkund Ghat Entry 2 — CCTV",        type:"Camera",   status:"Live",    value:"9.2/m²",  freshness:"2s ago"  },
  { id:"IOT-042", name:"Panchvati Ghat — Pressure Sensor",    type:"IoT",      status:"Live",    value:"74 kPa",  freshness:"4s ago"  },
  { id:"CAM-028", name:"Godavari Bridge — Overhead Camera",   type:"Camera",   status:"Live",    value:"8.1/m²",  freshness:"3s ago"  },
  { id:"WEA-01",  name:"IMD Weather Station — Nashik",        type:"Weather",  status:"Live",    value:"38.2°C",  freshness:"30s ago" },
  { id:"RIV-01",  name:"Godavari River Level — Ramkund",      type:"Sensor",   status:"Live",    value:"7.4m",    freshness:"10s ago" },
  { id:"GPS-182", name:"Police Fleet GPS — Zone Ramkund",     type:"GPS",      status:"Live",    value:"42 units",freshness:"5s ago"  },
  { id:"CAM-055", name:"Trimbakeshwar — Entry Gate Camera",   type:"Camera",   status:"Live",    value:"7.6/m²",  freshness:"3s ago"  },
  { id:"IOT-091", name:"Nashik Rd Parking — Sensor Array",    type:"IoT",      status:"Delayed", value:"71%",     freshness:"2m ago"  },
];

const INFRASTRUCTURE = [
  { name:"Ramkund Ghat Entry Gates (4)",  type:"Gate",    status:"Operational", load:94, risk:"critical" },
  { name:"Godavari Bridge",               type:"Bridge",  status:"Open",        load:81, risk:"high"     },
  { name:"Panchvati Bridge",              type:"Bridge",  status:"Open",        load:68, risk:"moderate" },
  { name:"Camp Alpha — Medical",          type:"Medical", status:"Near Capacity",load:84, risk:"high"    },
  { name:"Camp Beta — Medical",           type:"Medical", status:"Operational", load:52, risk:"moderate" },
  { name:"Shelter Camp 3 (NR)",           type:"Shelter", status:"94% Full",    load:94, risk:"critical" },
  { name:"Shuttle Bay — Nashik Road",     type:"Transit", status:"Operational", load:74, risk:"moderate" },
  { name:"Emergency Generator — Zone A",  type:"Power",   status:"On Standby",  load:12, risk:"normal"   },
];

const WEATHER = [
  { param:"Temperature",  value:"38.2°C", status:"High",   icon:Thermometer, color:"text-red-600",    bg:"bg-red-50" },
  { param:"Humidity",     value:"62%",    status:"High",   icon:Droplets,    color:"text-blue-600",   bg:"bg-blue-50" },
  { param:"Wind Speed",   value:"12 km/h",status:"Normal", icon:Wind,        color:"text-slate-600",  bg:"bg-slate-50" },
  { param:"Rain Forecast",value:"8%",     status:"Low",    icon:CloudRain,   color:"text-sky-600",    bg:"bg-sky-50"  },
  { param:"River Level",  value:"7.4m",   status:"Watch",  icon:Droplets,    color:"text-amber-600",  bg:"bg-amber-50"},
  { param:"UV Index",     value:"9 — High",status:"High",  icon:Zap,         color:"text-orange-600", bg:"bg-orange-50"},
];

const MULTI_SCENARIO_COMPARE = [
  { metric:"Peak Crowd",        scenario1:"9,00,000", scenario2:"6,20,000", scenario3:"6,84,000", scenario4:"4,20,000" },
  { metric:"Critical Zones",    scenario1:"5",        scenario2:"3",        scenario3:"7",        scenario4:"4"        },
  { metric:"Evac Time",         scenario1:"32 min",   scenario2:"26 min",   scenario3:"21 min",   scenario4:"38 min"   },
  { metric:"Police Required",   scenario1:"2,400",    scenario2:"1,800",    scenario3:"2,840",    scenario4:"1,600"    },
  { metric:"Medical Units",     scenario1:"12",       scenario2:"8",        scenario3:"14",       scenario4:"10"       },
  { metric:"Peak Risk Index",   scenario1:"9.2/10",   scenario2:"7.4/10",   scenario3:"Critical", scenario4:"6.8/10"  },
  { metric:"Buses Needed",      scenario1:"280",      scenario2:"340",      scenario3:"400",      scenario4:"180"      },
  { metric:"SLA Feasibility",   scenario1:"Feasible", scenario2:"Feasible", scenario3:"Stretch",  scenario4:"Feasible" },
];

const RESOURCE_PREPOSITION = [
  { zone:"Ramkund Ghat",    police:420, health:12, ndrf:2, buses:45, shelterSlots:800,  readiness:94 },
  { zone:"Panchvati",       police:340, health:10, ndrf:1, buses:38, shelterSlots:600,  readiness:88 },
  { zone:"Trimbakeshwar",   police:280, health:8,  ndrf:1, buses:32, shelterSlots:400,  readiness:82 },
  { zone:"Nashik Road Stn", police:220, health:6,  ndrf:1, buses:28, shelterSlots:300,  readiness:91 },
  { zone:"Kalaram Temple",  police:160, health:5,  ndrf:0, buses:20, shelterSlots:200,  readiness:86 },
  { zone:"Gangapur Camp",   police:140, health:4,  ndrf:0, buses:16, shelterSlots:500,  readiness:95 },
];

const SIM_TIMELINE = [
  { time:"T+0",   event:"Simulation started — Scenario: Shahi Snan Peak",    type:"start",   impact:"High"   },
  { time:"T+8m",  event:"Ramkund Entry 2 density exceeds 9/m²",              type:"alert",   impact:"Critical"},
  { time:"T+12m", event:"AI triggers crowd diversion — Entry 2 → Entry 4",   type:"action",  impact:"High"   },
  { time:"T+18m", event:"Police cordon deployed at Godavari Bridge south",    type:"action",  impact:"Medium" },
  { time:"T+24m", event:"Medical pre-position activated — Camp Alpha",        type:"action",  impact:"Medium" },
  { time:"T+32m", event:"Crowd density begins falling — diversion working",   type:"resolved",impact:"Low"    },
  { time:"T+45m", event:"Shelter Camp Delta opened — overflow resolved",      type:"resolved",impact:"Low"    },
  { time:"T+60m", event:"Risk Index drops below 7.0 — normalized",           type:"resolved",impact:"Low"    },
];

const MODEL_ACCURACY = [
  { scenario:"Nashik 2015 — Snan 1", predicted:1080000, actual:1100000, accuracy:98.2 },
  { scenario:"Nashik 2015 — Snan 2", predicted:1025000, actual:1050000, accuracy:97.6 },
  { scenario:"Ujjain 2016",          predicted:940000,  actual:950000,  accuracy:98.9 },
  { scenario:"Trimbak 2015",         predicted:812000,  actual:820000,  accuracy:99.0 },
  { scenario:"Test Run — Jun 2027",  predicted:618000,  actual:624000,  accuracy:99.0 },
];

const SHAHI_SNAN = [
  { snan:"Shahi Snan 1 — Simhastha Pournima", date:"29 Jul 2027", daysLeft:50, expCrowd:"12,00,000", riskPredict:"9.4/10", status:"Planning"  },
  { snan:"Shahi Snan 2 — Shravani Pournima",  date:"28 Aug 2027", daysLeft:80, expCrowd:"9,50,000",  riskPredict:"8.8/10", status:"Planned"   },
  { snan:"Shahi Snan 3 — Bhadrapada",         date:"11 Sep 2027", daysLeft:94, expCrowd:"8,00,000",  riskPredict:"8.1/10", status:"Planned"   },
  { snan:"Today — Regular Snan",              date:"09 Jun 2027", daysLeft:0,  expCrowd:"8,10,000",  riskPredict:"7.8/10", status:"Active"    },
];

// ── helpers ───────────────────────────────────────────────────────
const RISK_BUCKET = { CRITICAL:"critical", critical:"critical", HIGH:"serious", high:"serious", MED:"warning", moderate:"warning" };
const riskBg = r => statusClasses(RISK_BUCKET[r] || "good");

const ROUTE_STATUS_BUCKET = { Restricted:"critical", Moderate:"warning" };
const routeStatusColor = s => statusHex(ROUTE_STATUS_BUCKET[s] || "good");

const LOAD_BANDS = [ { min:90, bucket:"critical" }, { min:75, bucket:"serious" }, { min:55, bucket:"warning" }, { min:0, bucket:"good" } ];
const READINESS_BANDS = [ { min:90, bucket:"good" }, { min:80, bucket:"info" }, { min:0, bucket:"warning" } ];
const SNAN_STATUS_BUCKET = { Active:"info", Planning:"warning" };
const SNAN_RISK_BANDS = [ { min:9, bucket:"critical" }, { min:8, bucket:"serious" }, { min:0, bucket:"warning" } ];

const timelineColor = t =>
  t === "start"    ? "bg-blue-100 text-blue-600"    :
  t === "alert"    ? "bg-red-100 text-red-600"      :
  t === "action"   ? "bg-violet-100 text-violet-600":
  "bg-emerald-100 text-emerald-600";

const timelineIcon = t =>
  t === "start"    ? Play :
  t === "alert"    ? AlertTriangle :
  t === "action"   ? Shield :
  CheckCircle;

// Zones this page names also have a live density/risk read on Crowd
// Intelligence — link to it so the pre-positioning table isn't a dead end.
const ZONE_SLUG = {
  "Ramkund Ghat":"Ramkund", "Panchvati":"Panchvati", "Trimbakeshwar":"Trimbak",
  "Nashik Road Stn":"Nashik Rd", "Kalaram Temple":"Kalaram", "Gangapur Camp":"Gangapur",
};
const ZoneLink = ({ zone, children }) => {
  const slug = ZONE_SLUG[zone];
  if (!slug) return <>{children}</>;
  return (
    <Link to={`/crowd-intelligence?zone=${encodeURIComponent(slug)}`}
      className="hover:text-blue-700 hover:underline decoration-dotted underline-offset-2">
      {children}
    </Link>
  );
};

export default function DigitalTwin() {
  const { twin } = useIntelligence({ running: false });
  const t = useTranslate();
  const [running, setRunning]       = useState(null);
  const [expanded, setExpanded]     = useState(null);
  const [simTime, setSimTime]       = useState(0);
  const [activeTab, setActiveTab]   = useState("compare");

  const runningScenario = simulationScenarios.find(s => s.id === running);

  // Computed from the real arrays rendered below, not hand-typed — so the
  // headline KPIs and banner stats can't silently drift from their tables.
  const liveSensorCount = SENSOR_FEEDS.filter(s => s.status === "Live").length;
  const modelAccuracyAvg = (
    MODEL_ACCURACY.reduce((sum, m) => sum + m.accuracy, 0) / MODEL_ACCURACY.length
  ).toFixed(1);
  const minEvacTime = Math.min(...EVACUATION_ROUTES.map(r => r.time));
  const nextShahiSnanDays = SHAHI_SNAN.find(s => s.daysLeft > 0)?.daysLeft ?? 0;

  return (
    <div className="space-y-8">
      <SectionHeader icon="GitBranch" title="Crowd Digital Twin Simulation"
        subtitle="360° operational simulation · peak snan · bridge closure · flood · mass evacuation · resource pre-positioning · model calibration · Shahi Snan planning"
      />


      <LiveTwinSection twin={twin} />

      <StaticContentDivider />
      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
        <MetricCard title="Active Scenario"   value={running ? "RUNNING" : "Standby"} icon="Play"       color={running ? "green" : "slate"} index={0} live={!!running} />
        <MetricCard title="Scenarios Ready"   value={simulationScenarios.length.toString()} icon="GitBranch"  color="blue"   index={1} />
        <MetricCard title="Min Evac Time"     value={`${minEvacTime} min`}            icon="Timer"      color="amber"  index={2} />
        <MetricCard title="Model Accuracy"    value={`${modelAccuracyAvg}%`}          icon="Activity"   color="green"  index={3} />
        <MetricCard title="Sensor Feeds"      value={`${liveSensorCount}/${SENSOR_FEEDS.length} Live`} icon="Wifi"       color="purple" index={4} />
        <MetricCard title="Next Shahi Snan"   value={`${nextShahiSnanDays} days`}     icon="Calendar"   color="red"    index={5} />
      </div>

      {/* Twin Engine Status Banner */}
      <motion.div initial={{ opacity:0, y:-6 }} animate={{ opacity:1, y:0 }}
        className="rounded-2xl bg-white border border-slate-200 shadow-card p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <div className="w-2 h-2 rounded-full bg-emerald-400 live-dot" />
              <span className="text-[10px] font-black text-saffron-dark tracking-widest">{t("DIGITAL TWIN ENGINE — LIVE")}</span>
            </div>
            <h3 className="text-base font-black text-slate-900">{t("Maha Kumbh AI Digital Twin v3.2 · Nashik Simhastha 2027")}</h3>
            <p className="text-xs text-slate-500 mt-1 max-w-xl">
              {t("Real-time crowd simulation · Monte Carlo risk modelling · 8 live sensor feeds · Agent-based crowd behaviour · Weather-adjusted · River-level aware")}
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center text-xs">
            {[
              { label:"Model Ver",   val:"v3.2.1"   },
              { label:"Data Lag",    val:"2.8s"      },
              { label:"Accuracy",    val:`${modelAccuracyAvg}%` },
              { label:"Agents",      val:"6,84,210"  },
            ].map((s, i) => {
              const c = googleColor(i);
              return (
                <div key={s.label} className="rounded-xl px-3 py-2 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200"
                  style={{ background:c.bgLight, border:`1px solid ${c.border}` }}>
                  <div className="text-base font-black" style={{ color:c.text }}>{s.val}</div>
                  <div className="text-[9px] mt-0.5" style={{ color:c.text }}>{t(s.label)}</div>
                </div>
              );
            })}
          </div>
        </div>
      </motion.div>

      {/* Scenario Cards */}
      <div>
        <h3 className="text-sm font-black text-slate-900 mb-4">{t("Simulation Scenarios")}</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {simulationScenarios.map((s, i) => {
            const detail = SCENARIO_DETAILS[s.id];
            const isExp  = expanded === s.id;
            const isRun  = running === s.id;
            return (
              <motion.div key={s.id}
                initial={{ opacity:0, y:8 }} animate={{ opacity:1, y:0 }} transition={{ delay: i * 0.08 }}
                className={`card border-2 transition-all ${
                  isRun ? "border-blue-400 shadow-card-hover" : "border-transparent hover:border-blue-200"
                }`}
              >
                <div className="p-4 cursor-pointer" onClick={() => setExpanded(isExp ? null : s.id)}>
                  <div className="flex items-start justify-between mb-2">
                    <span className={`text-[10px] font-black px-2 py-1 rounded-lg border ${riskBg(s.riskLevel)}`}>{t(s.riskLevel)}</span>
                    {isRun && <span className="badge badge-live text-[9px]"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500 live-dot" />{t("Running")}</span>}
                  </div>
                  <h4 className="text-sm font-black text-slate-900 mb-2 leading-snug">{t(s.name)}</h4>
                  <div className="space-y-1 text-[10px] text-slate-600">
                    <div className="flex justify-between"><span>{t("Crowd Est.")}</span><span className="font-bold text-slate-800">{s.crowdEst}</span></div>
                    <div className="flex justify-between"><span>{t("Critical Zones")}</span><span className="font-bold text-red-600">{s.criticalZones}</span></div>
                    <div className="flex justify-between"><span>{t("Evac Time")}</span><span className="font-bold text-amber-600">{s.evacTime}</span></div>
                    {detail && <div className="flex justify-between"><span>{t("Peak Risk")}</span><span className="font-bold text-red-600">{detail.riskPeak}</span></div>}
                  </div>
                  <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100">
                    <span className={`badge text-[9px] ${s.status === "Ready" ? "badge-success" : "badge-info"}`}>{t(s.status)}</span>
                    <div className="flex items-center gap-1">
                      <button onClick={e => { e.stopPropagation(); setExpanded(isExp ? null : s.id); }}
                        className="text-[9px] text-slate-500 hover:text-blue-600 flex items-center gap-0.5">
                        {isExp ? <ChevronUp size={10} /> : <ChevronDown size={10} />} {t("Detail")}
                      </button>
                      <button onClick={e => { e.stopPropagation(); setRunning(isRun ? null : s.id); setSimTime(0); }}
                        className={`text-[10px] font-black flex items-center gap-1 px-2.5 py-1 rounded-lg transition-colors ${
                          isRun ? "bg-red-100 text-red-700 hover:bg-red-200" : "gov-gradient text-white hover:opacity-90"
                        }`}>
                        {isRun ? <><Pause size={9} /> {t("Stop")}</> : <><Play size={9} /> {t("Run")}</>}
                      </button>
                    </div>
                  </div>
                </div>
                <AnimatePresence>
                  {isExp && detail && (
                    <motion.div initial={{ height:0, opacity:0 }} animate={{ height:"auto", opacity:1 }}
                      exit={{ height:0, opacity:0 }} transition={{ duration:0.2 }}
                      className="overflow-hidden border-t border-slate-100">
                      <div className="p-4 bg-slate-50 rounded-b-2xl space-y-3 text-[10px]">
                        <div>
                          <div className="font-black text-slate-600 uppercase mb-1">{t("Trigger")}</div>
                          <p className="text-slate-700">{t(detail.trigger)}</p>
                        </div>
                        <div>
                          <div className="font-black text-slate-600 uppercase mb-1">{t("Inputs")}</div>
                          {detail.inputs.map((inp, j) => (
                            <div key={j} className="flex items-start gap-1 text-slate-600"><span className="text-blue-500 mt-0.5">›</span>{t(inp)}</div>
                          ))}
                        </div>
                        <div>
                          <div className="font-black text-slate-600 uppercase mb-1">{t("Expected Outcomes")}</div>
                          {detail.outcomes.map((o, j) => (
                            <div key={j} className="flex items-start gap-1 text-slate-600"><span className="text-amber-500 mt-0.5">›</span>{t(o)}</div>
                          ))}
                        </div>
                        <div>
                          <div className="font-black text-slate-600 uppercase mb-1">{t("Pre-positioning Actions")}</div>
                          {detail.preposition.map((p, j) => (
                            <div key={j} className="flex items-start gap-1 text-emerald-700 font-semibold"><CheckCircle size={9} className="mt-0.5 flex-shrink-0" />{t(p)}</div>
                          ))}
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Twin Map + Projection */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChartCard
          title="Digital Twin — Live Simulation Overlay"
          subtitle={runningScenario ? `${t("Running")}: ${t(runningScenario.name)}` : "Idle — select a scenario and press Run"}
          live={!!running}>
          <MapPlaceholder height={340} showHeatmap={!!running} />
          {running ? (
            <div className="mt-3 space-y-2">
              <div className="flex items-center gap-2 p-2.5 bg-blue-50 rounded-xl border border-blue-200">
                <div className="w-2 h-2 rounded-full bg-blue-600 live-dot" />
                <span className="text-xs font-bold text-blue-700 flex-1">
                  {t("Simulation running")} · T+{String(simTime).padStart(2,"0")}:00 · {t(runningScenario?.name)}
                </span>
                <button onClick={() => setRunning(null)} className="text-[10px] text-red-600 font-black flex items-center gap-1">
                  <Pause size={10} />{t("Stop")}
                </button>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center text-[10px]">
                {[
                  { label:"Risk Index", val:"8.4/10", color:"text-red-600"    },
                  { label:"Evacuated",  val:"0",      color:"text-blue-700"   },
                  { label:"T Elapsed",  val:"T+0",    color:"text-slate-700"  },
                  { label:"Next Event", val:"T+8m",   color:"text-amber-600"  },
                ].map(s => (
                  <div key={s.label} className="bg-slate-50 rounded-xl p-2 border border-slate-200">
                    <div className={`text-sm font-black ${s.color}`}>{s.val}</div>
                    <div className="text-[9px] text-slate-500">{t(s.label)}</div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="mt-3 p-3 rounded-xl border border-dashed border-slate-300 text-center text-xs text-slate-400">
              {t("Select a scenario above and press")} <strong>{t("Run")}</strong> {t("to start simulation")}
            </div>
          )}
        </ChartCard>

        <ChartCard title="Scenario Projection — 3 Hour Window"
          subtitle="Simulated crowd density, flow, risk and evacuated count over time">
          <p className="text-[10px] text-slate-400 -mt-3 mb-2">
            {t("This projection runs on the twin engine —")} <a href="#model-calibration" className="text-blue-600 hover:underline">
            {t("validated at")} {modelAccuracyAvg}% {t("accuracy against 5 past events, below")}</a>
          </p>
          <ResponsiveContainer width="100%" height={300}>
            <ComposedChart data={SCENARIO_PROJECTIONS} margin={{ top:5, right:10, left:-10, bottom:0 }}>
              <defs>
                <linearGradient id="dtFlowGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={chartColor(0)} stopOpacity={0.2} />
                  <stop offset="100%" stopColor={chartColor(0)} stopOpacity={0} />
                </linearGradient>
                <linearGradient id="dtEvacGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={statusHex("good")} stopOpacity={0.2} />
                  <stop offset="100%" stopColor={statusHex("good")} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid {...gridProps} />
              <XAxis dataKey="hour" tick={axisTick} />
              <YAxis yAxisId="left" tickFormatter={v=>`${(v/1000).toFixed(0)}K`} tick={axisTick} />
              <YAxis yAxisId="right" orientation="right" domain={[0,10]} tick={axisTick} />
              <Tooltip content={<ChartTooltip formatter={(v, n) => [n === t("Risk Index") ? `${v}/10` : v?.toLocaleString(), n]} />} />
              <Legend wrapperStyle={legendStyle} />
              <Area yAxisId="left" type="monotone" dataKey="crowdFlow"  fill="url(#dtFlowGrad)" stroke={chartColor(0)} strokeWidth={2} name={t("Crowd Flow")} />
              <Area yAxisId="left" type="monotone" dataKey="evacuated" fill="url(#dtEvacGrad)" stroke={statusHex("good")} strokeWidth={2} name={t("Evacuated")} />
              <Line yAxisId="left"  type="monotone" dataKey="density"  stroke={chartColor(3)} strokeWidth={2} dot={{ r:3 }} name={t("Density %")} />
              <Line yAxisId="right" type="monotone" dataKey="risk"     stroke={statusHex("critical")} strokeWidth={2.5} dot={{ r:3 }} name={t("Risk Index")} />
            </ComposedChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Simulation Event Timeline */}
      <ChartCard title="Simulation Event Timeline"
        subtitle="Key trigger events and response actions during the active scenario">
        <div className="relative pl-6">
          <div className="absolute left-2.5 top-0 bottom-0 w-0.5 bg-slate-200" />
          <div className="space-y-3">
            {SIM_TIMELINE.map((e, i) => {
              const Icon = timelineIcon(e.type);
              const cls  = timelineColor(e.type);
              return (
                <motion.div key={i}
                  initial={{ opacity:0, x:-8 }} animate={{ opacity:1, x:0 }} transition={{ delay: i * 0.06 }}
                  className="relative flex items-start gap-3"
                >
                  <div className={`absolute -left-6 w-5 h-5 rounded-full flex items-center justify-center ${cls}`}>
                    <Icon size={10} />
                  </div>
                  <div className="flex-1 p-3 rounded-xl border border-slate-200 hover:shadow-card-hover transition-shadow">
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] font-black text-blue-700 font-mono">{e.time}</span>
                        <span className={`text-[9px] font-black px-1.5 py-0.5 rounded-full ${cls}`}>{t(e.type.toUpperCase())}</span>
                        <span className={`text-[9px] font-bold ${
                          e.impact === "Critical" ? "text-red-600" : e.impact === "High" ? "text-orange-600" :
                          e.impact === "Medium"   ? "text-amber-600" : "text-emerald-600"
                        }`}>{t(e.impact)}</span>
                      </div>
                    </div>
                    <p className="text-xs text-slate-700 font-semibold mt-0.5">{t(e.event)}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </ChartCard>

      {/* Analysis Tabs */}
      <div>
        <div className="flex gap-2 mb-4 flex-wrap">
          {[
            { id:"compare",    label:"Multi-Scenario Comparison" },
            { id:"resources",  label:"Resource Pre-positioning"  },
            { id:"sensors",    label:"Live Sensor Feeds"         },
            { id:"infra",      label:"Infrastructure Status"     },
          ].map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`text-xs font-black px-4 py-2 rounded-xl transition-colors ${
                activeTab === tab.id ? "gov-gradient text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}>{t(tab.label)}</button>
          ))}
        </div>

        <AnimatePresence mode="wait">

          {/* Multi-Scenario Comparison */}
          {activeTab === "compare" && (
            <motion.div key="compare" initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}>
              <ChartCard title="Multi-Scenario Comparison Matrix"
                subtitle="Side-by-side comparison of all 4 simulation scenarios across key operational metrics">
                <div className="overflow-x-auto">
                  <table className="gov-table">
                    <thead>
                      <tr>
                        <th>{t("Metric")}</th>
                        <th className="text-orange-700 bg-orange-50">{t("Shahi Snan Peak")}</th>
                        <th className="text-amber-700 bg-amber-50">{t("Bridge Closure")}</th>
                        <th className="text-red-700 bg-red-50">{t("Mass Evacuation")}</th>
                        <th className="text-blue-700 bg-blue-50">{t("Flood Scenario")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {MULTI_SCENARIO_COMPARE.map((row, i) => (
                        <motion.tr key={row.metric} initial={{ opacity:0 }} animate={{ opacity:1 }} transition={{ delay: i * 0.05 }}>
                          <td className="font-bold text-slate-800">{t(row.metric)}</td>
                          <td className={`font-semibold ${
                            row.scenario1.includes("Critical") || row.scenario1.includes("Stretch") ? "text-red-600 font-black" : ""
                          }`}>{t(row.scenario1)}</td>
                          <td>{t(row.scenario2)}</td>
                          <td className={`font-semibold ${
                            row.scenario3 === "Critical" || row.scenario3 === "Stretch" ? "text-red-600 font-black" : ""
                          }`}>{t(row.scenario3)}</td>
                          <td>{t(row.scenario4)}</td>
                        </motion.tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </ChartCard>
            </motion.div>
          )}

          {/* Resource Pre-positioning */}
          {activeTab === "resources" && (
            <motion.div key="resources" initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}>
              <ChartCard title="AI Resource Pre-positioning Recommendations"
                subtitle="Optimal unit deployment per zone based on simulation outcomes — human approval required">
                <div className="overflow-x-auto">
                  <table className="gov-table">
                    <thead>
                      <tr>
                        <th>{t("Zone")}</th><th>{t("Police")}</th><th>{t("Health Teams")}</th>
                        <th>{t("NDRF")}</th><th>{t("Buses")}</th><th>{t("Shelter Slots")}</th><th>{t("Readiness")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {RESOURCE_PREPOSITION.map((r, i) => (
                        <motion.tr key={r.zone} initial={{ opacity:0 }} animate={{ opacity:1 }} transition={{ delay: i * 0.06 }}>
                          <td className="font-bold text-slate-900"><ZoneLink zone={r.zone}>{t(r.zone)}</ZoneLink></td>
                          <td className="text-blue-700 font-semibold">{r.police}</td>
                          <td className="text-emerald-700 font-semibold">{r.health}</td>
                          <td className="text-red-700 font-bold">{r.ndrf}</td>
                          <td className="text-amber-700 font-semibold">{r.buses}</td>
                          <td className="text-slate-700">{r.shelterSlots.toLocaleString()}</td>
                          <td>
                            <div className="flex items-center gap-2">
                              <div className="w-16 h-1.5 rounded-full bg-slate-100">
                                <div className="h-1.5 rounded-full" style={{
                                  width:`${r.readiness}%`,
                                  background: statusHex(thresholdStatus(r.readiness, READINESS_BANDS))
                                }} />
                              </div>
                              <span className="text-[10px] font-black">{r.readiness}%</span>
                            </div>
                          </td>
                        </motion.tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="mt-4">
                  <ResponsiveContainer width="100%" height={180}>
                    <BarChart data={RESOURCE_PREPOSITION} margin={{ top:5, right:10, left:-10, bottom:0 }}>
                      <CartesianGrid {...gridProps} />
                      <XAxis dataKey="zone" tick={{ ...axisTick, fontSize:8 }} tickFormatter={v => t(v)} />
                      <YAxis tick={axisTick} />
                      <Tooltip content={<ChartTooltip />} />
                      <Legend wrapperStyle={legendStyle} />
                      <Bar dataKey="police"  fill={chartColor(0)} radius={[3,3,0,0]} name={t("Police")}  />
                      <Bar dataKey="buses"   fill={chartColor(1)} radius={[3,3,0,0]} name={t("Buses")}   />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </ChartCard>
            </motion.div>
          )}

          {/* Sensor Feeds */}
          {activeTab === "sensors" && (
            <motion.div key="sensors" initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}>
              <ChartCard title="Live Data Feeds Powering the Digital Twin"
                subtitle="Cameras, IoT sensors, weather, GPS — real-time inputs to the simulation model">
                <p className="text-[10px] text-slate-400 -mt-1 mb-3">
                  {liveSensorCount} {t("of")} {SENSOR_FEEDS.length} {t("feeds live")} — {SENSOR_FEEDS.length - liveSensorCount} {t("delayed, shown below")}
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {SENSOR_FEEDS.map((s, i) => (
                    <motion.div key={s.id}
                      initial={{ opacity:0, y:4 }} animate={{ opacity:1, y:0 }} transition={{ delay: i * 0.06 }}
                      className={`p-3 rounded-xl border ${s.status === "Delayed" ? "border-amber-200 bg-amber-50/30" : "border-slate-200"}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-0.5">
                            <span className="text-[9px] font-black text-slate-400 font-mono">{s.id}</span>
                            <span className={`text-[8px] font-black px-1.5 py-0.5 rounded-full ${
                              s.type === "Camera"  ? "bg-blue-100 text-blue-700"    :
                              s.type === "IoT"     ? "bg-violet-100 text-violet-700":
                              s.type === "Weather" ? "bg-sky-100 text-sky-700"      :
                              s.type === "GPS"     ? "bg-emerald-100 text-emerald-700" :
                              "bg-amber-100 text-amber-700"
                            }`}>{t(s.type)}</span>
                          </div>
                          <p className="text-xs font-semibold text-slate-800">{t(s.name)}</p>
                          <p className="text-[10px] text-slate-500 mt-0.5">{t("Last")}: {t(s.freshness)}</p>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <div className="text-base font-black text-slate-900">{s.value}</div>
                          <span className={`text-[9px] font-black px-1.5 py-0.5 rounded-full ${
                            s.status === "Live" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"
                          }`}>{t(s.status)}</span>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </ChartCard>
            </motion.div>
          )}

          {/* Infrastructure Status */}
          {activeTab === "infra" && (
            <motion.div key="infra" initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}>
              <ChartCard title="Infrastructure Status Board"
                subtitle="Bridges, gates, medical camps, shelters and transit — current operational state">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {INFRASTRUCTURE.map((inf, i) => (
                    <motion.div key={inf.name}
                      initial={{ opacity:0, x:-4 }} animate={{ opacity:1, x:0 }} transition={{ delay: i * 0.06 }}
                      className={`p-3 rounded-xl border ${
                        inf.risk === "critical" ? "border-red-200 bg-red-50/30" :
                        inf.risk === "high"     ? "border-orange-200 bg-orange-50/20" : "border-slate-200"
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div>
                          <div className="text-xs font-bold text-slate-800">{t(inf.name)}</div>
                          <div className="text-[10px] text-slate-500">{t(inf.type)} · {t(inf.status)}</div>
                        </div>
                        <span className={`text-[9px] font-black px-1.5 py-0.5 rounded-full border flex-shrink-0 ${riskBg(inf.risk)}`}>
                          {t(inf.risk.toUpperCase())}
                        </span>
                      </div>
                      <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                        <motion.div className="h-2 rounded-full"
                          initial={{ width:0 }} animate={{ width:`${inf.load}%` }} transition={{ duration:0.8, delay: i * 0.06 }}
                          style={{ background: statusHex(thresholdStatus(inf.load, LOAD_BANDS)) }}
                        />
                      </div>
                      <div className="text-right text-[9px] font-bold text-slate-600 mt-0.5">{inf.load}%</div>
                    </motion.div>
                  ))}
                </div>
              </ChartCard>
            </motion.div>
          )}

        </AnimatePresence>
      </div>

      {/* Evacuation Route Analysis */}
      <ChartCard title="Evacuation Route Analysis"
        subtitle="Route capacity, clearance time, vehicle types and current load status">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
          {EVACUATION_ROUTES.map((r, i) => (
            <motion.div key={r.route}
              initial={{ opacity:0, x:-8 }} animate={{ opacity:1, x:0 }} transition={{ delay: i * 0.06 }}
              className={`p-3 rounded-xl border transition-shadow hover:shadow-card-hover ${
                r.status === "Restricted" ? "border-red-200 bg-red-50/30" :
                r.status === "Moderate"   ? "border-amber-200 bg-amber-50/20" : "border-slate-200"
              }`}
            >
              <div className="flex items-start justify-between gap-2 mb-1.5">
                <p className="text-xs font-bold text-slate-800">{t(r.route)}</p>
                <span className={`text-[9px] font-black px-1.5 py-0.5 rounded-full flex-shrink-0 ${statusClasses(ROUTE_STATUS_BUCKET[r.status] || "good")}`}>{t(r.status)}</span>
              </div>
              <div className="h-2 rounded-full bg-slate-100 overflow-hidden mb-1.5">
                <div className="h-2 rounded-full transition-all" style={{
                  width:`${r.load}%`, background: routeStatusColor(r.status)
                }} />
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-1 text-[9px] text-slate-500">
                <span className="flex items-center gap-0.5"><Clock size={8} />{r.time}{t("m clear")}</span>
                <span className="flex items-center gap-0.5"><Users size={8} />{(r.capacity/1000).toFixed(0)}{t("K cap")}</span>
                <span className="font-bold text-slate-700">{r.load}{t("% load")}</span>
                <span className="text-blue-600">{r.lanes} {t("lanes")}</span>
              </div>
              <div className="text-[9px] text-slate-400 mt-1">{t(r.vehicles)}</div>
            </motion.div>
          ))}
        </div>
        <ResponsiveContainer width="100%" height={160}>
          <BarChart data={EVACUATION_ROUTES} margin={{ top:5, right:10, left:-10, bottom:0 }}>
            <CartesianGrid {...gridProps} />
            <XAxis dataKey="route" tick={{ ...axisTick, fontSize:7 }} tickFormatter={v => t(v)} />
            <YAxis tick={axisTick} unit="%" domain={[0,100]} />
            <Tooltip content={<ChartTooltip />} />
            <Legend wrapperStyle={legendStyle} />
            <Bar dataKey="load" radius={[3,3,0,0]} name={t("Load %")}>
              {EVACUATION_ROUTES.map((r, i) => (
                <Cell key={i} fill={routeStatusColor(r.status)} />
              ))}
            </Bar>
            <Bar dataKey="time" fill={chartColor(1)} radius={[3,3,0,0]} name={t("Clearance (min)")} />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      {/* Weather Panel */}
      <div>
        <h3 className="text-sm font-black text-slate-900 mb-3">{t("Environmental Conditions — Simulation Inputs")}</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
          {WEATHER.map((w, i) => {
            const Icon = w.icon;
            return (
              <motion.div key={w.param}
                initial={{ opacity:0, y:6 }} animate={{ opacity:1, y:0 }} transition={{ delay: i * 0.07 }}
                className={`card p-4 border text-center ${
                  w.status === "High" || w.status === "Watch" ? "border-amber-200" : "border-slate-200"
                }`}
              >
                <div className={`w-9 h-9 rounded-full mx-auto flex items-center justify-center mb-2 ${w.bg}`}>
                  <Icon size={16} className={w.color} />
                </div>
                <div className={`text-sm font-black mb-0.5 ${w.color}`}>{w.value}</div>
                <div className="text-[10px] font-bold text-slate-600">{t(w.param)}</div>
                <div className={`text-[9px] font-bold mt-1 ${
                  w.status === "High" || w.status === "Watch" ? "text-amber-600" : "text-emerald-600"
                }`}>{t(w.status)}</div>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Model Accuracy + Shahi Snan Planning */}
      <div id="model-calibration" className="grid grid-cols-1 lg:grid-cols-2 gap-6 scroll-mt-20">
        <ChartCard title="Model Calibration & Accuracy"
          subtitle="Predicted vs actual crowd counts from past Kumbh events used to train the twin">
          <div className="space-y-3">
            {MODEL_ACCURACY.map((m, i) => (
              <motion.div key={m.scenario}
                initial={{ opacity:0, y:4 }} animate={{ opacity:1, y:0 }} transition={{ delay: i * 0.07 }}
                className="p-3 rounded-xl border border-slate-200"
              >
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-xs font-bold text-slate-800">{t(m.scenario)}</span>
                  <span className={`text-sm font-black ${m.accuracy >= 99 ? "text-emerald-600" : "text-blue-700"}`}>
                    {m.accuracy}%
                  </span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-[10px] text-center">
                  <div>
                    <div className="font-black text-blue-700">{(m.predicted/1000).toFixed(0)}K</div>
                    <div className="text-slate-500">{t("Predicted")}</div>
                  </div>
                  <div>
                    <div className="font-black text-slate-800">{(m.actual/1000).toFixed(0)}K</div>
                    <div className="text-slate-500">{t("Actual")}</div>
                  </div>
                  <div>
                    <div className="font-black text-emerald-600">{Math.abs(m.actual - m.predicted).toLocaleString()}</div>
                    <div className="text-slate-500">{t("Error")}</div>
                  </div>
                </div>
                <div className="mt-1.5 h-1.5 rounded-full bg-slate-100">
                  <div className="h-1.5 rounded-full bg-emerald-500" style={{ width:`${m.accuracy}%` }} />
                </div>
              </motion.div>
            ))}
          </div>
        </ChartCard>

        <ChartCard title="Shahi Snan Planning Board"
          subtitle="Upcoming Simhastha 2027 major bathing dates · AI crowd and risk predictions">
          <div className="space-y-3">
            {SHAHI_SNAN.map((s, i) => (
              <motion.div key={s.snan}
                initial={{ opacity:0, x:6 }} animate={{ opacity:1, x:0 }} transition={{ delay: i * 0.07 }}
                className={`p-4 rounded-xl border transition-all ${
                  s.status === "Active"   ? "border-blue-300 bg-blue-50 shadow-card-hover" :
                  i === 0                 ? "border-red-200 bg-red-50/30" : "border-slate-200"
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                      <span className={`text-[9px] font-black px-1.5 py-0.5 rounded-full ${statusClasses(SNAN_STATUS_BUCKET[s.status] || "neutral")}`}>{t(s.status)}</span>
                      {s.daysLeft > 0 && (
                        <span className="text-[9px] text-slate-500">
                          T–{s.daysLeft} {t("days")}
                        </span>
                      )}
                    </div>
                    <p className="text-xs font-black text-slate-900">{t(s.snan)}</p>
                    <p className="text-[10px] text-slate-500 mt-0.5">{s.date}</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="text-sm font-black text-slate-900">{s.expCrowd}</div>
                    <div className="text-[9px] text-slate-500">{t("Expected crowd")}</div>
                    <div className="text-xs font-black mt-0.5" style={{ color: statusHex(thresholdStatus(parseFloat(s.riskPredict), SNAN_RISK_BANDS)) }}>{s.riskPredict} {t("risk")}</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </ChartCard>
      </div>

      {/* Readiness Gauges */}
      <div>
        <h3 className="text-sm font-black text-slate-900 mb-3">{t("Operational Readiness — 6 Dimensions")}</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
          <GaugeCard title="Evac Readiness"    value={82} index={0} />
          <GaugeCard title="Route Clearance"   value={74} index={1} />
          <GaugeCard title="Medical Readiness" value={88} index={2} />
          <GaugeCard title="Police Deploy"     value={94} index={3} />
          <GaugeCard title="Shelter Capacity"  value={71} index={4} />
          <GaugeCard title="Flood Risk"        value={24} subtitle="Lower = safer" index={5} inverse />
        </div>
      </div>
    </div>
  );
}





